import { IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { GlobalLoader } from "./components/GlobalLoader";
import useHandleNotificationClick from "./hooks/useHandleNotificationClick";
import { useUserProjects } from "./hooks/useProjects";
import { IProject } from "./interfaces/project.interface";
import { queryClient } from "./lib/queryClient";
import authService from "./services/auth.service";
import { userGroupSvc } from "./services/userGroup.service";
import { registerFirebaseServiceWorker } from "./serviceWorkerRegistration";
import { useAppDispatch, useAppSelector } from "./store/hooks/hooks";
import { setProjects, setSelectedProject } from "./store/slices/projectSlice";
import { setUser, setUserGroups } from "./store/slices/userSlice";
import { pageview } from "./utils/analytic";
import { getFCMToken, requestNotificationPermission } from "./utils/fb";
import { startNetworkMonitoring } from "./utils/networkMonitor";
import ToastService from "./utils/toast";

interface IAppProps {
  msalInstance: IPublicClientApplication;
  msalScope: string;
  settings: any;
}

type Orientation = "portrait" | "landscape";

interface OrientationContextType {
  orientation: Orientation;
  width: number;
  height: number;
}

const OrientationContext = createContext<OrientationContextType>({
  orientation: "portrait",
  width: 0,
  height: 0,
});

export const useOrientation = () => useContext(OrientationContext);

interface OrientationProviderProps {
  children: ReactNode;
}

export const OrientationProvider: React.FC<OrientationProviderProps> = ({
  children,
}) => {
  const [orientation, setOrientation] = useState<Orientation>(
    window.innerHeight > window.innerWidth ? "portrait" : "landscape"
  );
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setDimensions({ width, height });
      setOrientation(height > width ? "portrait" : "landscape");
    };

    // Set initial values
    handleResize();

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Clean up event listeners
    return () => {
      window.removeEventListener("resize", handleResize);
      window.addEventListener("orientationchange", handleResize);
    };
  }, []);

  return (
    <OrientationContext.Provider
      value={{
        orientation,
        width: dimensions.width,
        height: dimensions.height,
      }}
    >
      {children}
    </OrientationContext.Provider>
  );
};

function App({ msalInstance, msalScope, settings }: IAppProps) {
  const user = useAppSelector((state) => state.user.user);
  const { instance } = useMsal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isInitialized, setIsInitialized] = useState(false);

  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  useHandleNotificationClick();

  const { selectedProject } = useAppSelector((state) => state.projects);

  var { data: projectsData } = useUserProjects(
    {},
    {
      enabled: !!user,
      showLoader: false,
    }
  );

  useEffect(() => {
    const storedParams = JSON.parse(
      localStorage.getItem("pendingNotification") || "{}"
    );
    if (storedParams.areaId && storedParams.levelId && storedParams.projectId) {
      console.log(
        "[Push] Redirecting from App.tsx with stored params:",
        storedParams
      );
      navigate(
        `/home?areaId=${storedParams.areaId}&levelId=${storedParams.levelId}&projectId=${storedParams.projectId}`
      );
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Handle the redirect promise first
        const response = await msalInstance.handleRedirectPromise();
        console.log("Response: ",response)
        if (response && response.account) {
          // Handle successful authentication
          const res: any = await authService.verifyToken(response.accessToken);
          if (res.data) {
            dispatch(
              setUser({
                user: res.data.user,
                token: res.data.token,
                refreshToken: res.data.refreshToken,
              })
            );
            navigate("/home", { replace: true });
          } else {
            ToastService.error(res.error);
          }
        } else {
          // Check for existing user session
          const user = localStorage.getItem("user");
          const atToken = localStorage.getItem("atToken");
          const rtToken = localStorage.getItem("rtToken");

          if (user && atToken && rtToken) {
            dispatch(
              setUser({
                user: JSON.parse(user),
                token: atToken,
                refreshToken: rtToken,
              })
            );
            // navigate("/home");
          } else {
            // Check auth settings
            const authData = settings.auth;
            const isCustomEnabled = authData.some(
              (auth: any) => auth.type === "Custom" && auth.enabled
            );

            if (!isCustomEnabled) {
              handleMsalLogin();
            }
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("Authentication initialization error:", error);
        setIsInitialized(true); // Set initialized even on error to prevent infinite loading
      }
    };
    startNetworkMonitoring();
    initializeAuth();
  }, []);

  const handleMsalLogin = () => {
    if (!isInitialized) return;
    const loginRequest = { scopes: [msalScope] };
    instance
      .loginRedirect(loginRequest)
      .catch((error) => console.error("Login error:", error));
  };

  useEffect(() => {
    if (!user) return;

    // projectsSvc.getUsersProjects().then((res) => {
    //   if (res.data?.length) {
    //     dispatch(setProjects(res.data as unknown as IProject[]));
    //     dispatch(setSelectedProject(res.data[0]));
    //   }
    // });

    initializeNotifications();

    userGroupSvc.getMyUserGroups().then((res) => {
      if (res.data) {
        dispatch(setUserGroups(res.data));
      }
    });
  }, [user]);

  useEffect(() => {
    if (projectsData?.data?.length) {
      dispatch(setProjects(projectsData.data as unknown as IProject[]));
      // if (!selectedProject) {
      dispatch(setSelectedProject(projectsData.data[0]));
    }
  }, [projectsData?.data]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "NOTIFICATION_RECEIVED") {
          // Invalidate all queries in the cache
          queryClient.invalidateQueries();
        }
      });
    }
  }, [queryClient]);

  const initializeNotifications = () => {
    registerFirebaseServiceWorker();

    requestNotificationPermission().then(async (granted) => {
      if (granted) {
        await getFCMToken();
      }
    });
  };

  if (!isInitialized) {
    return <GlobalLoader isStartup={true} />;
  }

  return (
    <OrientationProvider>
      <Outlet />
    </OrientationProvider>
  );
}

export default App;
